import IntegrationType from "../types/integration/IntegrationType";

export type NotificationIntegrationKind = 'email' | 'pagerduty' | 'servicenow' | 'slack' | 'webhook';

export interface NotificationIntegrationConfig {
    kind: NotificationIntegrationKind;
    basePrefix: string;
    label: string;
    requiredParameter: string;
    // Admin route for this integration's profiles, e.g. "/admin/integrations/email".
    // Profile screens hang off it: "/create", "/edit/<prefix>", "/delete/<prefix>".
    path: string;
}

export interface NotificationIntegrationProfile {
    prefix: string;
    index: number;
    label: string;
    isDefault: boolean;
    enabled: boolean;
    isSet: boolean;
    values: Record<string, string | boolean | undefined>;
    parameters: IntegrationType[];
}

export const NOTIFICATION_PROFILE_PROPERTY: Record<NotificationIntegrationKind, string> = {
    email: 'email.profile',
    pagerduty: 'pagerduty.profile',
    servicenow: 'servicenow.profile',
    slack: 'slack.profile',
    webhook: 'webhook.profile'
};

export const NOTIFICATION_INTEGRATIONS: Record<NotificationIntegrationKind, NotificationIntegrationConfig> = {
    email: {
        kind: 'email',
        basePrefix: 'mail.smtp',
        label: 'Email',
        requiredParameter: 'host',
        path: '/admin/integrations/email'
    },
    pagerduty: {
        kind: 'pagerduty',
        basePrefix: 'pagerduty',
        label: 'PagerDuty',
        requiredParameter: 'url',
        path: '/admin/integrations/pager-duty'
    },
    servicenow: {
        kind: 'servicenow',
        basePrefix: 'servicenow',
        label: 'ServiceNow',
        requiredParameter: 'url',
        path: '/admin/integrations/service-now'
    },
    slack: {
        kind: 'slack',
        basePrefix: 'slack',
        label: 'Slack',
        requiredParameter: 'url',
        path: '/admin/integrations/slack'
    },
    webhook: {
        kind: 'webhook',
        basePrefix: 'webhook',
        label: 'Webhook',
        requiredParameter: 'url',
        path: '/admin/integrations/webhooks'
    }
};

const escapedPrefix = (prefix: string) => prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getProfilePrefixPattern = (basePrefix: string): RegExp => {
    return new RegExp(`^(${escapedPrefix(basePrefix)})(\\d*)\\.`);
};

export const isProfileParameter = (name: string, basePrefix: string): boolean => {
    return getProfilePrefixPattern(basePrefix).test(name);
};

export const profileIndexFromPrefix = (prefix: string, basePrefix: string): number => {
    if (prefix === basePrefix) {
        return 1;
    }

    const suffix = prefix.substring(basePrefix.length);
    const index = Number(suffix);
    return Number.isFinite(index) && index > 1 ? index : 1;
};

export const profilePrefixForIndex = (basePrefix: string, index: number): string => {
    return index <= 1 ? basePrefix : `${basePrefix}${index}`;
};

export const getParameterKey = (name: string, prefix: string): string => {
    return name.substring(prefix.length + 1);
};

export const normalizeParameterValue = (value: any): string | boolean | undefined => {
    return value === 'true' ? true : value === 'false' ? false : value;
};

export const getProfileValue = (
    profile: NotificationIntegrationProfile | undefined,
    key: string
): string | boolean | undefined => {
    return profile?.values[key];
};

export const parseNotificationProfiles = (
    parameters: IntegrationType[],
    config: NotificationIntegrationConfig
): NotificationIntegrationProfile[] => {
    const groups = new Map<string, IntegrationType[]>();
    const pattern = getProfilePrefixPattern(config.basePrefix);

    parameters
        .filter(parameter => pattern.test(parameter.name))
        .forEach(parameter => {
            const match = parameter.name.match(pattern);
            const prefix = match ? `${match[1]}${match[2] || ''}` : config.basePrefix;
            groups.set(prefix, [...(groups.get(prefix) ?? []), parameter]);
        });

    return Array.from(groups.entries())
        .map(([prefix, group]) => {
            const values: Record<string, string | boolean | undefined> = {};
            group.forEach(parameter => {
                values[getParameterKey(parameter.name, prefix)] = normalizeParameterValue(parameter.value);
            });

            const index = profileIndexFromPrefix(prefix, config.basePrefix);
            const configuredLabel = values['dbmarlin.name']?.toString().trim();

            return {
                prefix,
                index,
                label: configuredLabel || `${config.label}${index === 1 ? '' : ` ${index}`}`,
                isDefault: index === 1,
                enabled: values.enable === true,
                isSet: !!values[config.requiredParameter],
                values,
                parameters: group
            };
        })
        .filter(profile => profile.isSet || Object.keys(profile.values).length > 0)
        .sort((a, b) => a.index - b.index);
};

export const getNextProfilePrefix = (
    parameters: IntegrationType[],
    config: NotificationIntegrationConfig
): string => {
    const profiles = parseNotificationProfiles(parameters, config);
    const used = new Set(profiles.map(profile => profile.index));
    let index = 1;

    while (used.has(index)) {
        index++;
    }

    return profilePrefixForIndex(config.basePrefix, index);
};

export const findProfile = (
    parameters: IntegrationType[],
    config: NotificationIntegrationConfig,
    prefix?: string
): NotificationIntegrationProfile | undefined => {
    const requestedPrefix = prefix || config.basePrefix;
    return parseNotificationProfiles(parameters, config)
        .find(profile => profile.prefix === requestedPrefix);
};

export const remapProfileParameterName = (
    name: string,
    fromPrefix: string,
    toPrefix: string
): string => {
    return `${toPrefix}.${getParameterKey(name, fromPrefix)}`;
};

export const buildMakeDefaultPayloads = (
    parameters: IntegrationType[],
    config: NotificationIntegrationConfig,
    selectedPrefix: string
): { deleteValues: { name: string }[]; postValues: { name: string; value: any }[] } => {
    const profiles = parseNotificationProfiles(parameters, config);
    const defaultProfile = profiles.find(profile => profile.prefix === config.basePrefix);
    const selectedProfile = profiles.find(profile => profile.prefix === selectedPrefix);

    if (!selectedProfile || selectedProfile.isDefault) {
        return { deleteValues: [], postValues: [] };
    }

    const deleteValues = [
        ...(defaultProfile?.parameters ?? []),
        ...selectedProfile.parameters
    ].map(parameter => ({ name: parameter.name }));

    const postValues = [
        ...selectedProfile.parameters.map(parameter => ({
            name: remapProfileParameterName(parameter.name, selectedProfile.prefix, config.basePrefix),
            value: parameter.value, encrypted: true
        })),
        ...(defaultProfile?.parameters ?? []).map(parameter => ({
            name: remapProfileParameterName(parameter.name, config.basePrefix, selectedProfile.prefix),
            value: parameter.value, encrypted: true
        }))
    ];

    return { deleteValues, postValues };
};
